import React, { useEffect, useState } from 'react';

import { ReactComponent as LockIcon } from '../../assets/lock.svg';
import { ReactComponent as RightArrow } from '../../assets/right-arrow.svg';
import { useAuth } from '../../contexts/auth';
import { UserGroup, UserSession } from '../../contexts/auth/types';
import { LoginFormModal } from '../login-form-modal';
import {
	button as buttonStyle,
	container as containerStyle,
	iconContainer as iconContainerStyle,
	wrapper as wrapperStyle,
} from './styles.module.css';

export enum LoginWrapperState {
	Unknown,
	NotLoggedIn,
	LoggedInButNotAuthorized,
	Authorized,
}

interface LoginWrapperProps {
	allowedGroups?: Array<string>;
	children?: React.ReactNode;
}

const userIsAuthorized = (session: UserSession, allowedGroups?: Array<string>) =>
	allowedGroups
		? allowedGroups.some((allowedGroup) => session?.groups?.includes(allowedGroup as UserGroup))
		: true;

const LoginWrapperNotLoggedIn = () => {
	const { login, resetPassword } = useAuth();
	const [showLoginModal, setShowLoginModal] = useState(false);

	const handleOnCloseLoginModal = () => setShowLoginModal(false);

	const handleOnClickLogInNow = () => setShowLoginModal(true);

	const handleOnLogin = async (username: string, password: string) => {
		if (!username || !password) throw new Error('Username and password are required to log in');
		const { kind } = await login(username, password);
		if (kind !== 'loggedIn') return;
		setShowLoginModal(false);
	};

	const handleResetPassword = async (username: string) => {
		if (!username) throw new Error('Username is required to reset password');
		try {
			await resetPassword(username);
			setShowLoginModal(false);
		} catch (error) {
			console.error(error);
			throw error;
		}
	};

	return (
		<>
			<div className={containerStyle}>
				<div className={wrapperStyle}>
					<div className={iconContainerStyle}>
						<LockIcon />
					</div>
					<p>
						If you work in this industry and have a user account, you can log in to access more
						information.
					</p>
					<button type="submit" className={buttonStyle} onClick={handleOnClickLogInNow}>
						Log in now
						<RightArrow />
					</button>
				</div>
			</div>
			<LoginFormModal
				open={showLoginModal}
				onLogin={handleOnLogin}
				onResetPassword={handleResetPassword}
				onClose={handleOnCloseLoginModal}
			/>
		</>
	);
};

const LoginWrapperLoggedInButNotAuthorized = () => {
	const { logout } = useAuth();

	const handleOnClickLogOut = () => logout();

	return (
		<div className={containerStyle}>
			<div className={wrapperStyle}>
				<div className={iconContainerStyle}>
					<LockIcon />
				</div>
				<p>Sorry, you are not authorized to view this content.</p>
				<button className={buttonStyle} onClick={handleOnClickLogOut}>
					Log Out
					<RightArrow />
				</button>
			</div>
		</div>
	);
};

export const LoginWrapper = ({ allowedGroups, children }: LoginWrapperProps) => {
	const { session } = useAuth();
	const [loginState, setLoginState] = useState<LoginWrapperState>(LoginWrapperState.Unknown);

	useEffect(() => {
		if (!session) {
			setLoginState(LoginWrapperState.NotLoggedIn);
			return;
		}
		setLoginState(
			userIsAuthorized(session, allowedGroups)
				? LoginWrapperState.Authorized
				: LoginWrapperState.LoggedInButNotAuthorized
		);
	}, [session]);

	switch (loginState) {
		case LoginWrapperState.NotLoggedIn:
			return <LoginWrapperNotLoggedIn />;

		case LoginWrapperState.LoggedInButNotAuthorized:
			return <LoginWrapperLoggedInButNotAuthorized />;

		case LoginWrapperState.Authorized:
			return <>{children}</>;

		case LoginWrapperState.Unknown:
			return <p>Loading...</p>;
	}
};
